export type job_emp_type = 'full_time' | 'part_time' | 'contract' | 'probation' | 'intern';
export type job_status   = 'open' | 'closed' | 'on_hold';
export type app_status   = 'applied' | 'shortlisted' | 'interviewed' | 'selected' | 'rejected';

export interface job_opening {
  id: number;
  job_code: string;
  title: string;
  department: string;
  emp_type: job_emp_type;
  positions: number;
  filled: number;
  description: string;
  requirements: string;
  posted_date: string;
  closing_date: string;
  status: job_status;
}

export interface job_application {
  id: number;
  job_code: string;
  job_title: string;
  applicant_name: string;
  email: string;
  phone: string;
  experience: number;
  applied_date: string;
  status: app_status;
  remarks: string;
}

export const JOB_EMP_TYPE_OPTS: job_emp_type[] = ['full_time', 'part_time', 'contract', 'probation', 'intern'];
export const JOB_STATUS_OPTS: job_status[] = ['open', 'on_hold', 'closed'];

export const JOB_TYPE_LABELS: Record<job_emp_type, string> = {
  full_time: 'Full time',
  part_time: 'Part time',
  contract: 'Contract',
  probation: 'Probation',
  intern: 'Intern',
};

export const JOB_STATUS_LABELS: Record<job_status, string> = {
  open: 'Open',
  on_hold: 'On hold',
  closed: 'Closed',
};

function iso_date(offset: number): string {
  const d = new Date();
  d.setDate(d.getDate() - offset);
  return d.toISOString().substring(0, 10);
}

export function job_public_path(job_code: string): string {
  return `/careers/${encodeURIComponent(job_code)}`;
}

export function job_public_url(job_code: string): string {
  if (typeof window === 'undefined') return job_public_path(job_code);
  return `${window.location.origin}${job_public_path(job_code)}`;
}

/*
export function seed_job_openings(): job_opening[] {
  return [
    {
      id: 1, job_code: 'JOB-2026-001', title: 'Physics Teacher', department: 'Science',
      emp_type: 'full_time', positions: 1, filled: 0, status: 'open',
      posted_date: iso_date(14), closing_date: iso_date(-14),
      description: 'Teach Physics for Classes 9–12 following the CBSE curriculum. Plan lessons, run laboratory practicals, assess student progress, and support board-exam preparation. You will also mentor the science club and help with inter-school Olympiad coaching.',
      requirements: 'M.Sc Physics with B.Ed (mandatory). Minimum 2 years of teaching experience at secondary or senior-secondary level. Comfort with lab work, digital lesson plans, and parent communication. CTET/TET preferred.',
    },
    {
      id: 2, job_code: 'JOB-2026-002', title: 'Accountant', department: 'Accounts & Finance',
      emp_type: 'full_time', positions: 1, filled: 0, status: 'open',
      posted_date: iso_date(20), closing_date: iso_date(-10),
      description: 'Manage day-to-day school accounting: fee receipts, vendor bills, payroll inputs, bank reconciliation, and monthly MIS for the principal. Work closely with HR Admin on salary processing and with the fee desk on concessions and dues.',
      requirements: 'B.Com or M.Com. Hands-on Tally / accounting-software experience. Knowledge of GST, TDS, and school fee accounting is preferred. Minimum 3 years of experience in education or a similar services organisation.',
    },
    {
      id: 3, job_code: 'JOB-2026-003', title: 'Lab Technician', department: 'Laboratory',
      emp_type: 'full_time', positions: 2, filled: 1, status: 'open',
      posted_date: iso_date(30), closing_date: iso_date(-7),
      description: 'Maintain Physics, Chemistry, and Biology labs. Prepare apparatus and reagents before practical periods, keep inventory and safety logs, and support teachers during board practicals. One of two positions is already filled.',
      requirements: 'B.Sc in a relevant science field. ITI or lab-technician certification preferred. Familiarity with school lab safety norms and basic equipment upkeep. Prior school or college lab experience is an advantage.',
    },
    {
      id: 4, job_code: 'JOB-2026-004', title: 'Sports Coach', department: 'Physical Education',
      emp_type: 'part_time', positions: 1, filled: 0, status: 'on_hold',
      posted_date: iso_date(7), closing_date: iso_date(-21),
      description: 'Coach cricket and football for middle and senior school. Run after-school practice, select teams for inter-school meets, and coordinate with the Physical Education department on sports day. This posting is currently on hold.',
      requirements: 'Relevant sports certification (NIS / state coaching diploma or equivalent). 3+ years of school or club coaching experience. First-aid certification preferred. Must be available on weekday afternoons and Saturday mornings.',
    },
  ];
}

export function seed_job_applications(): job_application[] {
  const a = (id: number, jc: string, jt: string, name: string, email: string, phone: string, exp: number, dOff: number, st: app_status, rem = ''): job_application => ({
    id, job_code: jc, job_title: jt, applicant_name: name, email, phone, experience: exp,
    applied_date: iso_date(dOff), status: st, remarks: rem,
  });
  return [
    a(1, 'JOB-2026-001', 'Physics Teacher', 'Kiran Desai',    'kiran@gmail.com',  '9801234567', 4, 10, 'shortlisted'),
    a(2, 'JOB-2026-001', 'Physics Teacher', 'Arjun Menon',    'arjun@gmail.com',  '9812345678', 2, 8,  'applied'),
    a(3, 'JOB-2026-001', 'Physics Teacher', 'Pooja Rathod',   'pooja@gmail.com',  '9823456789', 6, 12, 'interviewed', 'Round 1 done'),
    a(4, 'JOB-2026-002', 'Accountant',      'Ramesh Dixit',   'ramesh@gmail.com', '9834567890', 3, 7,  'applied'),
    a(5, 'JOB-2026-002', 'Accountant',      'Smita Kulkarni', 'smita@gmail.com',  '9845678901', 5, 9,  'shortlisted'),
    a(6, 'JOB-2026-002', 'Accountant',      'Vivek Goswami',  'vivek@gmail.com',  '9856789012', 2, 6,  'rejected', 'Experience mismatch'),
    a(7, 'JOB-2026-003', 'Lab Technician',  'Priya Kapoor',   'priyak@gmail.com', '9867890123', 1, 5,  'selected', 'Offer released'),
    a(8, 'JOB-2026-003', 'Lab Technician',  'Ajay Kumar',     'ajay@gmail.com',   '9878901234', 3, 8,  'shortlisted'),
  ];
}
*/